import { useState } from "react";
import { useTranslation } from "react-i18next";
import { InboxOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  notification,
  Row,
  Select,
  Upload,
} from "antd";
import { observer } from "mobx-react-lite";

import { createSupportRequest, type SupportData } from "../api/support";
import { SupportInfoAlert } from "../components/support/SupportInfoAlert";
import { SupportSuccessModal } from "../components/support/SupportSuccessModal";
import { SUPPORT_GROUP_VALUES } from "../config";
import { userStore } from "../stores/userStore";
import { createBeforeUpload } from "../utils/createBeforeUpload";
import { usePageTitle } from "../utils/usePageTitle";
import { usePendingAction } from "../utils/usePendingAction";

import "./SupportPage.css";

import type { UploadFile } from "antd";

const { Dragger } = Upload;

export const SupportPage = observer(function SupportPage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t("menu.support"));
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const { pending: saving, run: runSave } = usePendingAction();
  const [form] = Form.useForm();
  const userEmail = userStore.profile?.email ?? "";

  const supportGroupOptions = SUPPORT_GROUP_VALUES.map((value) => ({
    value,
    label: t(`support.categories.${value}`),
  }));

  const normFile = (e: UploadFile[] | { fileList: UploadFile[] }) => {
    if (Array.isArray(e)) return e;
    return e.fileList;
  };

  const getProductOptions = () => {
    const options = [
      { value: "my", label: t("support.products.account") },
      { value: "web", label: t("support.products.webgis") },
      { value: "data", label: t("support.products.data") },
      { value: "toolbox", label: t("support.products.toolbox") },
      { value: "connect", label: t("support.products.connect_plugin") },
      { value: "geoservices", label: t("support.products.geoservices") },
      { value: "mobile", label: t("support.products.mobile") },
      { value: "qgis", label: t("support.products.qgis") },
      { value: "other", label: t("support.products.other") },
    ];

    if (userStore.isRuRegion && i18n.language.startsWith("ru")) {
      options.splice(options.length - 1, 0, {
        value: "rosreestr_tools",
        label: t("support.products.rosreestr"),
      });
    }

    return options;
  };

  async function handleSave(values: SupportData) {
    await runSave(async () => {
      try {
        const formData = new FormData();
        const metadata = {
          group: values.supportGroup,
          product: values.product,
          subject: values.subject,
          description: values.description,
        };

        formData.append("metadata", JSON.stringify(metadata));

        if (values.files && values.files.length > 0) {
          values.files.forEach((fileItem: UploadFile) => {
            if (fileItem.originFileObj) {
              formData.append("files", fileItem.originFileObj);
            }
          });
        }

        const response = await createSupportRequest(formData);

        if (response.ok) {
          form.resetFields();
          setSuccessModalOpen(true);
        } else {
          notification.error({ message: t("support.requestError") });
        }
      } catch (error) {
        console.error(error);
        notification.error({ message: t("support.requestError") });
      }
    });
  }

  return (
    <div className="page support-page">
      <h1>{t("menu.support")}</h1>

      <Row>
        <Col xs={24} md={16}>
          <SupportInfoAlert />
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{}}
      >
        <Row gutter={32}>
          <Col xs={12} md={8}>
            <Form.Item
              name="supportGroup"
              label={t("support.chooseSupportGroup")}
              rules={[{ required: true, message: t("support.fieldRequired") }]}
            >
              <Select size="large" options={supportGroupOptions} />
            </Form.Item>
          </Col>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.supportGroup !== currentValues.supportGroup
            }
          >
            {({ getFieldValue }) => {
              const isSupport = getFieldValue("supportGroup") === "support";

              return isSupport ? (
                <Col xs={12} md={8}>
                  <Form.Item
                    name="product"
                    label={t("support.product")}
                    rules={[]}
                  >
                    <Select size="large" options={getProductOptions()} />
                  </Form.Item>
                </Col>
              ) : null;
            }}
          </Form.Item>
        </Row>

        <Row gutter={32}>
          <Col xs={24} md={16}>
            <Form.Item
              name="subject"
              label={t("support.subject")}
              rules={[{ required: true, message: t("support.fieldRequired") }]}
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item
              name="description"
              label={t("support.description")}
              rules={[{ required: true, message: t("support.fieldRequired") }]}
            >
              <Input.TextArea rows={4} size="large" />
            </Form.Item>

            <Form.Item
              name="files"
              label={t("support.attachments")}
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Dragger
                multiple={true}
                beforeUpload={createBeforeUpload(t)}
                accept=".jpg,.png,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">{t("support.addOrDropFiles")}</p>
              </Dragger>
            </Form.Item>
          </Col>
        </Row>

        <Button
          type="primary"
          size="large"
          htmlType="submit"
          loading={saving}
          // disabled={saving}
        >
          {t("support.submit")}
        </Button>
      </Form>
      <SupportSuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        email={userEmail}
      />
    </div>
  );
});
